import { useState } from "react";
import { Volume2, VolumeX, Play, Square, Check, BellRing, Sparkles, Music } from "lucide-react";
import { useOrderAlarm, SOUNDS } from "@/context/OrderAlarmContext";
import { toast } from "sonner";

export default function AdminAlarmSoundSettings() {
  const alarm = useOrderAlarm();
  const [playingId, setPlayingId] = useState(null);

  if (!alarm) return null;
  const { settings, persist, playAlarm, stopAlarm, requestPermission } = alarm;

  const currentSound = settings.sound || "cloche";
  const currentVolume = typeof settings.volume === "number" ? settings.volume : 0.7;
  const isEnabled = settings.enabled !== false;

  const handleToggle = () => {
    if (!isEnabled) requestPermission();
    persist({ enabled: !isEnabled });
    toast.success(!isEnabled ? "Alarme de commande activée" : "Alarme de commande désactivée");
  };

  const handleSelectSound = (id) => {
    persist({ sound: id });
    toast.success(`Sonnerie sélectionnée : ${SOUNDS.find((s) => s.id === id)?.label || id}`);
  };

  const handlePreview = (id) => {
    if (playingId === id) {
      stopAlarm();
      setPlayingId(null);
      return;
    }
    stopAlarm();
    setPlayingId(id);
    playAlarm(currentVolume, id);
    setTimeout(() => {
      setPlayingId((cur) => {
        if (cur === id) {
          stopAlarm();
          return null;
        }
        return cur;
      });
    }, 4000);
  };

  return (
    <div data-testid="admin-alarm-sound-page" className="max-w-4xl space-y-6">
      {/* En-tête */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-tr from-brand to-emerald-500 text-white shadow-lg shadow-emerald-500/20">
            <BellRing className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-ink">
              Signal Sonore d'Alerte Commande
            </h1>
            <p className="text-sm text-slate-500">
              Personnalisez la sonnerie d'alerte et l'alarme en temps réel dès qu'une commande est passée ou payée.
            </p>
          </div>
        </div>

        {/* Interrupteur On/Off principal */}
        <button
          type="button"
          data-testid="admin-alarm-main-toggle"
          onClick={handleToggle}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all shadow-sm ${
            isEnabled
              ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-500/20"
              : "bg-slate-200 text-slate-600 hover:bg-slate-300"
          }`}
        >
          <BellRing className="h-4 w-4" />
          {isEnabled ? "Alarme Active" : "Alarme Désactivée"}
        </button>
      </div>

      {/* Carte Contrôle du Volume */}
      <div className="rounded-2xl border border-line bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            {currentVolume > 0 ? (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                <Volume2 className="h-5 w-5 text-emerald-600" />
              </span>
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <VolumeX className="h-5 w-5" />
              </span>
            )}
            <div>
              <p className="text-sm font-bold text-ink">Volume de l'Alarme</p>
              <p className="text-xs text-slate-400">Niveau sonore appliqué lors des notifications d'arrivée</p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:w-64">
            <input
              type="range"
              min="0"
              max="100"
              data-testid="admin-alarm-volume-slider"
              value={Math.round(currentVolume * 100)}
              onChange={(e) => persist({ volume: Number(e.target.value) / 100 })}
              className="h-2 flex-1 cursor-pointer accent-emerald-500"
            />
            <span className="w-12 text-right font-mono text-sm font-bold text-slate-600">
              {Math.round(currentVolume * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* Grille de sélection des sonneries */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-ink flex items-center gap-2">
            <Music className="h-4 w-4 text-emerald-600" /> Choisir une sonnerie
          </h2>
          <span className="text-xs text-slate-400">
            {SOUNDS.length} signaux disponibles (Synthétisés & Audio WAV)
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SOUNDS.map((sound) => {
            const isSelected = currentSound === sound.id;
            const isPlaying = playingId === sound.id;

            return (
              <div
                key={sound.id}
                data-testid={`alarm-sound-card-${sound.id}`}
                onClick={() => handleSelectSound(sound.id)}
                className={`group relative flex cursor-pointer items-center justify-between rounded-2xl border p-4 transition-all ${
                  isSelected
                    ? "border-emerald-500 bg-emerald-50/50 shadow-sm ring-1 ring-emerald-500/20"
                    : "border-line bg-white hover:border-slate-300 hover:shadow-sm"
                }`}
              >
                <div className="flex items-center gap-3.5 pr-2">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                      isSelected
                        ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/25"
                        : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                    }`}
                  >
                    {isSelected ? <Check className="h-5 w-5 stroke-[2.5]" /> : <Music className="h-5 w-5" />}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-ink">{sound.label}</p>
                      {sound.type === "file" ? (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                          Fichier Audio
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          Web Audio
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{sound.desc}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    data-testid={`alarm-preview-${sound.id}`}
                    onClick={() => handlePreview(sound.id)}
                    title={isPlaying ? "Arrêter" : "Écouter l'aperçu"}
                    className={`flex h-9 w-9 items-center justify-center rounded-xl transition-all ${
                      isPlaying
                        ? "bg-rose-500 text-white shadow-md shadow-rose-500/30"
                        : isSelected
                        ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-ink"
                    }`}
                  >
                    {isPlaying ? <Square className="h-4 w-4 fill-white" /> : <Play className="h-4 w-4 fill-current ml-0.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Note d'information */}
      <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-r from-slate-50 to-white p-4 text-xs text-slate-500 flex items-start gap-3">
        <Sparkles className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
        <p>
          <strong className="text-slate-700">Fonctionnement en arrière-plan :</strong> Les alertes sonores se déclenchent lors de la réception d'une commande ou déclaration de paiement, même lorsque vous naviguez sur une autre page de l'administration. Vos réglages de sonnerie et volume sont enregistrés automatiquement sur votre navigateur.
        </p>
      </div>
    </div>
  );
}
